<template>
  <div class="space-y-8">
    <section v-if="!hasBusiness && !serviceListLoading" class="rounded-[32px] border border-orange-100 bg-[linear-gradient(180deg,#fffdfa_0%,#fff7f0_100%)] px-6 py-12 shadow-sm lg:px-10 lg:py-16">
      <div class="mx-auto flex max-w-3xl flex-col items-center text-center">
        <div class="flex h-20 w-20 items-center justify-center rounded-full bg-[linear-gradient(135deg,#fff1e3_0%,#ffffff_100%)] text-4xl shadow-sm ring-1 ring-orange-100">
          <i class="ri-building-4-line text-brand"></i>
        </div>
        <h2 class="mt-6 text-4xl font-semibold tracking-tight text-ink">Welcome to VendorHub</h2>
        <p class="mt-4 max-w-2xl text-lg leading-8 text-muted">
          Register your business entity, then add venue properties or service vendor brands under it.
        </p>

        <div class="mt-10 grid w-full max-w-2xl gap-4 rounded-[28px] border border-orange-100 bg-white p-6 shadow-sm md:grid-cols-3">
          <div class="flex flex-col items-center text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff4e8] text-2xl text-brand">
              <i class="ri-building-line"></i>
            </div>
            <p class="mt-3 text-lg font-semibold text-ink">Business</p>
            <p class="mt-1 text-sm text-muted">Legal entity, GST, bank</p>
          </div>
          <div class="flex flex-col items-center text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff4e8] text-2xl text-brand">
              <i class="ri-bank-line"></i>
            </div>
            <p class="mt-3 text-lg font-semibold text-ink">Venue</p>
            <p class="mt-1 text-sm text-muted">Property + spaces + menus</p>
          </div>
          <div class="flex flex-col items-center text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff4e8] text-2xl text-brand">
              <i class="ri-camera-line"></i>
            </div>
            <p class="mt-3 text-lg font-semibold text-ink">Service vendor</p>
            <p class="mt-1 text-sm text-muted">Photography, catering, decor...</p>
          </div>
        </div>

        <button
          type="button"
          class="mt-10 rounded-2xl bg-brand px-8 py-4 text-base font-semibold text-white shadow-sm transition hover:bg-brandDark"
          @click="navigateToBusinessEditor()"
        >
          Register your first business
        </button>
      </div>
    </section>

    <section v-else class="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div class="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-lg font-semibold text-ink">Businesses</h3>
          <p class="text-sm text-muted">Manage your business records here.</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <button type="button" class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" :disabled="serviceListLoading" @click="fetchBusinessVendors()">
            {{ serviceListLoading ? 'Refreshing...' : 'Refresh' }}
          </button>
          <button type="button" class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="navigateToBusinessEditor()">
            {{ hasBusiness ? 'Add another business' : 'Add business' }}
          </button>
        </div>
      </div>

      <div v-if="serviceListError" class="mb-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
        {{ serviceListError }}
      </div>

      <div v-if="serviceListLoading" class="rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-muted">
        Loading business vendors...
      </div>

      <TransitionGroup
        v-else-if="serviceVendors.length"
        tag="div"
        name="business-card"
        class="grid gap-4 md:grid-cols-2"
      >
        <article
          v-for="(business, index) in serviceVendors"
          :key="business.id"
          class="business-card-item group relative overflow-visible rounded-[20px] border border-[#eadfd4] bg-[linear-gradient(180deg,#fffdfb_0%,#fff8f2_100%)] p-4 shadow-[0_10px_26px_rgba(91,70,56,0.06)] transition duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-[0_18px_34px_rgba(91,70,56,0.12)]"
          :class="openBusinessMenuId === business.id ? 'z-50' : 'z-0'"
          :style="{ animationDelay: `${Math.min(index, 8) * 70}ms` }"
        >
          <div
            class="flex w-full cursor-pointer gap-4 text-left"
            role="button"
            tabindex="0"
            @click="navigateToBusinessDetail(business)"
            @keydown.enter.prevent="navigateToBusinessDetail(business)"
            @keydown.space.prevent="navigateToBusinessDetail(business)"
          >
            <div class="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-orange-100 bg-white sm:h-24 sm:w-24">
              <img
                :src="getBusinessImageSrc(business.image)"
                :alt="business.businessName"
                class="h-full w-full object-contain p-2 transition duration-300 group-hover:scale-105"
              >
            </div>

            <div class="min-w-0 flex-1">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate text-[1.1rem] font-semibold leading-tight text-ink sm:text-[1.2rem]">{{ business.businessName }}</p>
                  <p class="mt-1 text-xs font-semibold uppercase tracking-wide text-brand">{{ business.badge }}</p>
                </div>
                <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-white ring-1 ring-brand transition group-hover:bg-brand group-hover:text-white">
                  <i class="ri-arrow-right-up-line !text-white"></i>
                </span>
              </div>
              <div v-if="business.address" class="mt-3 text-sm leading-5 text-muted">
                <p>
                  <i class="ri-map-pin-2-line mr-1 text-brand"></i>
                  <span>{{ getBusinessAddressText(business) }}</span>
                  <button
                    v-if="shouldShowAddressToggle(business.address)"
                    type="button"
                    class="ml-2 inline-flex text-xs font-semibold text-brand transition hover:text-brandDark"
                    @click.stop="toggleAddressExpansion(business.id)"
                  >
                    {{ isBusinessAddressExpanded(business.id) ? 'Less' : 'More' }}
                  </button>
                </p>
              </div>
              <p v-else class="mt-3 text-sm text-muted">Address not added</p>
            </div>
          </div>

          <div class="mt-4 flex items-center justify-between gap-3 border-t border-[#eadfd4]/80 pt-3">
            <div class="flex flex-wrap gap-3">
              <button
                type="button"
                class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
                @click="navigateToBusinessDetail(business)"
              >
                <i class="ri-eye-line"></i>
                View details
              </button> 
            </div>
            <div class="relative">
              <button
                type="button"
                class="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-lg text-ink transition hover:border-brand hover:text-brand"
                @click="toggleBusinessMenu(business.id)"
              >
                <i class="ri-more-2-fill"></i>
              </button>
              <div
                v-if="openBusinessMenuId === business.id"
                class="absolute right-0 top-11 z-[80] min-w-[190px] rounded-2xl border border-gray-100 bg-white p-2 shadow-[0_18px_42px_rgba(31,18,8,0.18)]"
              > 
                <button type="button" class="flex w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-ink transition hover:bg-[#fff3e5] hover:text-brand" @click="handleVendorMenuAction('edit', business)">
                  Edit business
                </button>
                <button type="button" class="flex w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-red-600 transition hover:bg-red-50" @click="handleVendorMenuAction('delete', business)">
                  Delete business
                </button>
              </div>
            </div>
          </div>
        </article>
      </TransitionGroup>

    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import api from '@/services/vendor/api'
import { getAuthUserId } from '@/services/auth'

const router = useRouter()

const serviceVendors = ref([])
const vendorId = ref(getAuthUserId())
const serviceListLoading = ref(false)
const serviceListError = ref('')
const openBusinessMenuId = ref(null)
const expandedAddressIds = ref(new Set())
const BUSINESS_IMAGE_PLACEHOLDER = 'https://dummyimage.com/960x640/fff1e6/8b5e3c&text=Business'

const hasBusiness = computed(() => serviceVendors.value.length > 0)

const hasBusinessImage = (value) => {
  if (value == null) return false
  const normalized = String(value).trim()
  return normalized !== '' && normalized !== '0'
}

const ADDRESS_PREVIEW_LIMIT = 50
const getBusinessImageSrc = (value) => (hasBusinessImage(value) ? value : BUSINESS_IMAGE_PLACEHOLDER)
const shouldShowAddressToggle = (value) => String(value || '').trim().length > ADDRESS_PREVIEW_LIMIT
const isBusinessAddressExpanded = (businessId) => expandedAddressIds.value.has(String(businessId))
const getBusinessAddressText = (business) => {
  const address = String(business?.address || '').trim()
  if (!address || isBusinessAddressExpanded(business.id) || address.length <= ADDRESS_PREVIEW_LIMIT) {
    return address
  }
  return `${address.slice(0, ADDRESS_PREVIEW_LIMIT).trim()}...`
}
const toggleAddressExpansion = (businessId) => {
  const id = String(businessId)
  const next = new Set(expandedAddressIds.value)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  expandedAddressIds.value = next
}

const normalizeImage = (item) => item?.img || item?.image || item?.business_img || item?.photo || ''

const normalizeBusinessVendor = (item, index) => ({
  id: item?.id || item?.business_vendor_id || item?.vendor_id || item?.business_id || `vendor-${index}`,
  sId: item?.s_id || item?.seller_id || item?.vendor_owner_id || '',
  businessName: item?.business_name || item?.name || item?.title || 'Untitled business vendor',
  ownerName: item?.owner_name || item?.owner || item?.contact_name || 'Owner not available',
  description: item?.description || item?.about || item?.details || 'No description available.',
  image: normalizeImage(item),
  phoneCode: item?.phone_code ?? '',
  phone: item?.phone ?? '',
  email: item?.email ?? '',
  address: item?.address ?? item?.location ?? '',
  website: item?.website ?? '',
  latitude: item?.latitude ?? '',
  longitude: item?.longitude ?? '',
  doc1: item?.doc1 ?? '',
  doc2: item?.doc2 ?? '',
  doc3: item?.doc3 ?? '',
  badge: item?.status || item?.type || 'Business vendor'
})

const getListCollections = (data) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) {
    return {
      vendorItems: payload
    }
  }
  return {
    vendorItems:
      payload?.vendors ||
      payload?.business_vendors ||
      payload?.businessVendor ||
      payload?.items ||
      payload?.list ||
      []
  }
}

const fetchBusinessVendors = async () => {
  vendorId.value = getAuthUserId()
  serviceListError.value = ''

  if (!vendorId.value) {
    serviceListError.value = 'Vendor id (`s_id`) is not available in auth storage.'
    serviceVendors.value = []
    return
  }

  serviceListLoading.value = true
  try {
    const formData = new FormData()
    formData.append('s_id', vendorId.value)
    const { data } = await api.listBusinessVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Failed to load business vendors')
    }

    const collections = getListCollections(data)
    const normalizedVendors = Array.isArray(collections.vendorItems)
      ? collections.vendorItems.map(normalizeBusinessVendor)
      : []

    serviceVendors.value = normalizedVendors
  } catch (error) {
    serviceListError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to load business vendors'
    serviceVendors.value = []
  } finally {
    serviceListLoading.value = false
  }
}

const navigateToBusinessEditor = (business = null, tab = 'business') => {
  if (business?.id) {
    router.push({
      name: 'business-edit',
      params: { id: business.id },
      query: { tab }
    })
    return
  }

  router.push({
    name: 'business-create',
    query: { tab: 'business' }
  })
}

const navigateToBusinessDetail = (business = null) => {
  if (!business?.id) return
  router.push({
    name: 'business-detail',
    params: { id: business.id }
  })
}

const toggleBusinessMenu = (businessId) => {
  openBusinessMenuId.value = openBusinessMenuId.value === businessId ? null : businessId
}

const handleVendorMenuAction = (action, vendor) => {
  openBusinessMenuId.value = null
  if (action === 'view') {
    navigateToBusinessDetail(vendor)
    return
  }
  if (action === 'edit') {
    navigateToBusinessEditor(vendor, 'business')
    return
  }
  if (action === 'delete') {
    deleteServiceVendor(vendor.id)
  }
}

const deleteServiceVendor = async (id) => {
  const result = await Swal.fire({
    title: 'Delete business?',
    text: 'This business will be removed.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626'
  })

  if (!result.isConfirmed) return

  try {
    const formData = new FormData()
    formData.append('business_id', id)
    const { data } = await api.deleteBusinessVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Failed to delete business')
    }

    serviceVendors.value = serviceVendors.value.filter((item) => item.id !== id)
    await fetchBusinessVendors()
    await Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: data?.msg || 'Business deleted',
      showConfirmButton: false,
      timer: 1600,
      timerProgressBar: true
    })
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Delete failed',
      text: error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to delete business'
    })
  }
}

onMounted(() => {
  fetchBusinessVendors()
})
</script>

<style scoped>
.business-card-item {
  animation: business-card-enter 0.42s ease both;
}

.business-card-enter-active,
.business-card-leave-active {
  transition: opacity 0.24s ease, transform 0.24s ease;
}

.business-card-enter-from,
.business-card-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

.business-card-move {
  transition: transform 0.24s ease;
}

@keyframes business-card-enter {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
